import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';

// Auth components
import { AuthProvider, useAuth } from './contexts/SupabaseAuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';

// Layout components
import { DashboardLayout } from './components/layout/DashboardLayout';
import { AuthLayout } from './components/layout/AuthLayout';

// Page components
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { GoogleCallbackPage } from './pages/auth/GoogleCallbackPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { ProjectsPage } from './pages/projects/ProjectsPage';
import { ProjectDetailPage } from './pages/projects/ProjectDetailPage';
import { AgentsPage } from './pages/agents/AgentsPage';
import { ConversationPage } from './pages/agents/ConversationPage';
import { AnalyticsPage } from './pages/analytics/AnalyticsPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { ReportViewPage } from './pages/reports/ReportViewPage';
import { InsightDetailPage } from './pages/insights/InsightDetailPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { BillingPage } from './pages/billing/BillingPage';
import { OnboardingPage } from './pages/onboarding/OnboardingPage';

// Error components
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { NotFoundPage } from './pages/errors/NotFoundPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 401/403 errors
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
    mutations: {
      retry: false,
    },
  },
});

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading Crutan AI...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* OAuth callback route - needs to be accessible regardless of auth state */}
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />

      {/* Public routes */}
      <Route path="/auth/*" element={
        user ? <Navigate to="/dashboard" replace /> : <AuthRoutes />
      } />

      {/* Protected routes */}
      <Route path="/*" element={
        <ProtectedRoute>
          <DashboardRoutes />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function AuthRoutes() {
  return (
    <AuthLayout>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="*" element={<Navigate to="/auth/login" replace />} />
      </Routes>
    </AuthLayout>
  );
}

function DashboardRoutes() {
  const { user, userProfile, loading } = useAuth();
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);

  console.log('🔍 DashboardRoutes render:', {
    loading,
    hasUser: !!user,
    hasUserProfile: !!userProfile,
    userProfileOnboardingCompleted: userProfile?.onboardingCompleted,
    userEmail: user?.email
  });

  // Set a timeout for loading state to prevent infinite loading
  React.useEffect(() => {
    if (loading || (user && !userProfile)) {
      const timeout = setTimeout(() => {
        console.log('⚠️ Loading timeout reached - proceeding without profile');
        setLoadingTimeout(true);
      }, 15000); // 15 seconds timeout

      return () => clearTimeout(timeout);
    } else {
      setLoadingTimeout(false);
    }
  }, [loading, user, userProfile]);

  // Wait for user profile to load before making routing decisions
  // Show loading if:
  // 1. Auth is still loading, OR
  // 2. User exists but profile hasn't loaded yet
  // BUT: Don't show loading forever - timeout after 15 seconds
  if ((loading || (user && !userProfile)) && !loadingTimeout) {
    console.log('🔄 Showing loading state:', { loading, hasUser: !!user, hasUserProfile: !!userProfile });
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="flex flex-col items-center space-y-4 max-w-md text-center px-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading your profile...</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Setting up your account and workspace. This may take a few moments...
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
          >
            Taking too long? Click to refresh
          </button>
        </div>
      </div>
    );
  }

  // At this point, we have either:
  // 1. No user (will be handled by ProtectedRoute), OR
  // 2. User with loaded profile, OR
  // 3. User with loading timeout (profile failed to load)
  
  console.log('🔍 DashboardRoutes - Routing decision:', {
    hasUser: !!user,
    hasUserProfile: !!userProfile,
    userProfileOnboardingCompleted: userProfile?.onboardingCompleted,
    loadingTimeout,
    currentPath: window.location.pathname
  });
  
  // Check if user is currently on onboarding page
  const isOnOnboardingPage = window.location.pathname.startsWith('/onboarding');
  
  // CRITICAL FIX: Only make onboarding decisions if we have a loaded user profile
  // If userProfile is still loading (null), don't redirect to onboarding
  if (user && userProfile) {
    const onboardingCompleted = userProfile.onboardingCompleted;
    
    // If onboarding is completed and user is on onboarding page, redirect to dashboard
    if (onboardingCompleted && isOnOnboardingPage) {
      console.log('🔄 Onboarding completed but on onboarding page - redirecting to dashboard');
      return <Navigate to="/dashboard" replace />;
    }
    
    // Only redirect to onboarding if onboarding is explicitly not completed
    if (!onboardingCompleted && !isOnOnboardingPage) {
      console.log('🔄 Onboarding not completed - redirecting to onboarding');
      return (
        <Routes>
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/onboarding/:step" element={<OnboardingPage />} />
          <Route path="*" element={<Navigate to="/onboarding/1" replace />} />
        </Routes>
      );
    }
  }
  
  // If profile failed to load after timeout and user is trying to access onboarding, allow it
  if (user && !userProfile && loadingTimeout && isOnOnboardingPage) {
    console.log('🔄 Profile failed to load, allowing onboarding access');
    return (
      <Routes>
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/onboarding/:step" element={<OnboardingPage />} />
        <Route path="*" element={<Navigate to="/onboarding/1" replace />} />
      </Routes>
    );
  }

  return (
    <DashboardLayout>
      <Routes>
        {/* Dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Projects */}
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="/projects/:projectId/insights/:insightId" element={<InsightDetailPage />} />

        {/* AI Agents */}
        <Route path="/agents" element={<AgentsPage />} />
        <Route path="/agents/:agentType" element={<AgentsPage />} />
        <Route path="/agents/:agentType/conversation/:conversationId?" element={<ConversationPage />} />

        {/* Analytics */}
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/analytics/:projectId" element={<AnalyticsPage />} />

        {/* Reports */}
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/:reportId" element={<ReportViewPage />} />

        {/* Settings */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/:section" element={<SettingsPage />} />

        {/* Billing */}
        <Route path="/billing" element={<BillingPage />} />

        {/* Onboarding - always accessible for edge cases */}
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/onboarding/:step" element={<OnboardingPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </DashboardLayout>
  );
}

// Toast configuration component
function ToasterConfig() {
  const { theme } = useTheme();
  
  return (
    <Toaster 
      position="top-right"
      theme={theme}
      richColors={true}
      closeButton={true}
      expand={true}
      toastOptions={{
        duration: 4000,
        classNames: {
          toast: theme === 'dark' 
            ? 'bg-gray-800 border-gray-700 text-white shadow-lg'
            : 'bg-white border-gray-200 text-gray-900 shadow-lg',
          title: theme === 'dark' ? 'text-white' : 'text-gray-900',
          description: theme === 'dark' ? 'text-gray-300' : 'text-gray-600',
          actionButton: theme === 'dark' 
            ? 'bg-blue-600 hover:bg-blue-700 text-white' 
            : 'bg-blue-600 hover:bg-blue-700 text-white',
          cancelButton: theme === 'dark' 
            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
            : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
          closeButton: theme === 'dark' 
            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600' 
            : 'bg-gray-100 hover:bg-gray-200 text-gray-500 border-gray-300',
          success: theme === 'dark'
            ? 'bg-green-900/50 border-green-700/50 text-green-100'
            : 'bg-green-50 border-green-200 text-green-800',
          error: theme === 'dark'
            ? 'bg-red-900/50 border-red-700/50 text-red-100'
            : 'bg-red-50 border-red-200 text-red-800',
          warning: theme === 'dark'
            ? 'bg-yellow-900/50 border-yellow-700/50 text-yellow-100'
            : 'bg-yellow-50 border-yellow-200 text-yellow-800',
          info: theme === 'dark'
            ? 'bg-blue-900/50 border-blue-700/50 text-blue-100'
            : 'bg-blue-50 border-blue-200 text-blue-800',
          loading: theme === 'dark'
            ? 'bg-gray-800 border-gray-700 text-gray-100'
            : 'bg-white border-gray-200 text-gray-900',
        },
        style: {
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
        },
      }}
    />
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <ProjectProvider>
              <Router future={{ v7_relativeSplatPath: true }}>
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
                  <AppRoutes />
                  
                  {/* Global toast notifications with theme-aware styling */}
                  <ToasterConfig />
                </div>
              </Router>
            </ProjectProvider>
          </AuthProvider>
        </ThemeProvider>
        
        {/* React Query DevTools */}
        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App; 